import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import api from '../api/api'
import { CURRENCY, setVendorScopedCurrency } from '../config/currency'
import CategoryLayout from '../components/CategoryLayout'
import { useCart } from '../context/CartContext'
import { useAuth } from '../context/AuthContext'
import LoadingScreen from '../components/LoadingScreen'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import PortionSelector from '../components/PortionSelector'
import SmartPortionDropdown from '../components/SmartPortionDropdown'
import SimpleItemSelector from '../components/SimpleItemSelector'
import { CustomerRequestButton } from '../components/CustomerRequestButton'
import { QRCodeErrorHandler, getQRErrorType } from '../components/QRCodeErrorHandler'
import { useVendorBranding } from '../context/VendorBrandingContext'
import { DisabledButtonTooltip, useDisabledButtonMessage } from '../components/DisabledButtonTooltip'
import { MenuItem as MenuItemType, SPICE_LEVELS, SpiceLevel } from '../types/menu'
import Fuse from 'fuse.js'
import { useTheme } from '../context/ThemeContext'
import WindowsScrollArrows from '../components/WindowsScrollArrows'
import MenuItemImage from '../components/MenuItemImage'
import { EmptyState, PrimaryButton, CardContainer } from '../components/ui'
import { ALLERGEN_LABELS } from '../config/allergens'
import { DietaryDot } from '../components/DietaryDot'

// ============================================
// MODERN THEME: ITEM EMOJI/COLOR MAPPING
// Only used when modernDashboard theme is active
// ============================================
// Soft/subtle color palette - muted but still has character
const itemKeywordStyles: Array<{ keywords: string[]; emoji: string; color: string; gradient: string }> = [
  // Proteins - soft warm tones
  { keywords: ['chicken', 'murgh', 'murg'], emoji: '🍗', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
  { keywords: ['mutton', 'lamb', 'gosht', 'keema'], emoji: '🥩', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
  { keywords: ['fish', 'machli', 'pomfret', 'salmon'], emoji: '🐟', color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF 0%, #DBEAFE 100%)' },
  { keywords: ['prawn', 'shrimp', 'jhinga'], emoji: '🦐', color: '#B88888', gradient: 'linear-gradient(135deg, #FEF7F7 0%, #FDE8E8 100%)' },
  { keywords: ['egg', 'anda', 'omelette', 'omelet', 'benedict'], emoji: '🥚', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
  { keywords: ['paneer', 'cottage cheese'], emoji: '🧀', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
  // Rice - warm golden tones
  { keywords: ['biryani', 'pulao', 'pilaf', 'fried rice'], emoji: '🍚', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
  { keywords: ['rice', 'chawal'], emoji: '🍚', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
  // Noodles & Pasta - soft warm
  { keywords: ['noodle', 'chowmein', 'hakka'], emoji: '🍜', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
  { keywords: ['pasta', 'spaghetti', 'penne'], emoji: '🍝', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
  // Pizza & Burger - soft warm
  { keywords: ['pizza'], emoji: '🍕', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
  { keywords: ['burger'], emoji: '🍔', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
  { keywords: ['sandwich'], emoji: '🥪', color: '#7BA868', gradient: 'linear-gradient(135deg, #F7FEE7 0%, #ECFCCB 100%)' },
  { keywords: ['wrap', 'roll', 'kathi'], emoji: '🌯', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
  { keywords: ['toast', 'bread'], emoji: '🍞', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
  // Indian - warm earthy tones
  { keywords: ['naan', 'kulcha', 'roti', 'paratha', 'chapati'], emoji: '🫓', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
  { keywords: ['curry', 'masala', 'gravy', 'korma', 'tikka'], emoji: '🍛', color: '#B07858', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
  { keywords: ['dal', 'daal', 'lentil'], emoji: '🥘', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
  { keywords: ['samosa'], emoji: '🥟', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
  { keywords: ['tandoor', 'kebab', 'seekh'], emoji: '🔥', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
  // South Indian
  { keywords: ['dosa', 'dosai'], emoji: '🥞', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
  { keywords: ['idli', 'idly'], emoji: '⚪', color: '#6B7280', gradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)' },
  // Chinese
  { keywords: ['momo', 'dumpling'], emoji: '🥟', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
  { keywords: ['manchurian'], emoji: '🥦', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
  // Soups & Salads
  { keywords: ['soup'], emoji: '🍜', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
  { keywords: ['salad'], emoji: '🥗', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
  // Desserts - soft pink/purple
  { keywords: ['ice cream', 'gelato', 'sundae'], emoji: '🍨', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
  { keywords: ['cake', 'pastry', 'brownie'], emoji: '🍰', color: '#A87898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
  { keywords: ['gulab jamun', 'rasgulla', 'ladoo', 'jalebi'], emoji: '🍬', color: '#9888A8', gradient: 'linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 100%)' },
  // Drinks - soft cool tones
  { keywords: ['lassi', 'buttermilk'], emoji: '🥛', color: '#6B98A8', gradient: 'linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%)' },
  { keywords: ['juice', 'nimbu', 'lemonade'], emoji: '🧃', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
  { keywords: ['shake', 'smoothie', 'frappe'], emoji: '🥤', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
  { keywords: ['coffee', 'cappuccino', 'latte'], emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
  { keywords: ['tea', 'chai'], emoji: '🍵', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' },
  { keywords: ['cold drink', 'soda', 'coca', 'pepsi'], emoji: '🥤', color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF 0%, #DBEAFE 100%)' },
  // Snacks
  { keywords: ['fries', 'french fries', 'wedges'], emoji: '🍟', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
  { keywords: ['popcorn'], emoji: '🍿', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
  // Vegetables
  { keywords: ['aloo', 'potato'], emoji: '🥔', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
  { keywords: ['palak', 'spinach'], emoji: '🥬', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
  { keywords: ['avocado'], emoji: '🥑', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
  { keywords: ['tomato'], emoji: '🍅', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
]

// Soft fallback colors for items without keywords
const vibrantColors = [
  { color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
  { color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
  { color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
  { color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF 0%, #DBEAFE 100%)' },
  { color: '#9B8AB8', gradient: 'linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 100%)' },
  { color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
]

const getItemStyle = (itemName: string): { emoji: string; color: string; gradient: string } => {
  const normalizedName = itemName.toLowerCase()
  for (const mapping of itemKeywordStyles) {
    for (const keyword of mapping.keywords) {
      if (normalizedName.includes(keyword)) {
        return { emoji: mapping.emoji, color: mapping.color, gradient: mapping.gradient }
      }
    }
  }
  const hash = normalizedName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  const colorSet = vibrantColors[hash % vibrantColors.length]
  return { emoji: '🍽️', ...colorSet }
}
// ============================================

interface Portion {
  id: number
  portionName: string
  priceModifier: number
  isDefault: boolean
  displayOrder: number
  effectivePrice: number
  displayPrice: string
}

interface MenuItem extends MenuItemType {
  categoryId?: number
  categoryName?: string
  portions?: Portion[]
}

interface MenuCategory {
  id: number
  name: string
  items: MenuItem[]
  vendor: { id: number }
}

/** Small amber chips listing an item's declared allergens; renders nothing when undeclared. */
const AllergenChips = ({ allergens }: { allergens?: string[] }) => {
  if (!allergens || allergens.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1 mb-2">
      {allergens.map((a) => (
        <span
          key={a}
          className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200"
        >
          {ALLERGEN_LABELS[a] || a}
        </span>
      ))}
    </div>
  )
}

const Menu = () => {
  const { vendorId } = useParams<{ vendorId: string }>()
  const location = useLocation()
  const query = new URLSearchParams(location.search)
  // Support both 'qr' and 'qrCode' parameters for backward compatibility
  const qrCode = query.get('qrCode') || query.get('qr')
  // Also get vendorId from query params if not in path params
  const vendorIdFromQuery = query.get('vendorId')
  // QR code version support (v1 = legacy, v2 = new format with validation)
  const qrVersion = query.get('v') || '1'
  
  // WhatsApp auto-auth token
  const waToken = query.get('wa')
  const { isAuthenticated, isGuest, setWhatsAppGuestSession } = useAuth()
  const [waAuthAttempted, setWaAuthAttempted] = useState(false)

  // Handle WhatsApp token exchange on mount
  useEffect(() => {
    if (!waToken || waAuthAttempted) return;
    // Skip if already authenticated (vendor/steward/admin or guest)
    // Don't overwrite an existing session with a guest session
    if (isAuthenticated) {
      // Clean up the URL
      const params = new URLSearchParams(location.search);
      params.delete('wa');
      const newSearch = params.toString();
      window.history.replaceState({}, '', `${location.pathname}${newSearch ? '?' + newSearch : ''}`);
      setWaAuthAttempted(true);
      return;
    }

    setWaAuthAttempted(true);

    api.get('/guest-auth/whatsapp-token', { params: { token: waToken } })
      .then((res) => {
        setWhatsAppGuestSession(res.data);
        // Clean up the URL — remove ?wa= param
        const params = new URLSearchParams(location.search);
        params.delete('wa');
        const newSearch = params.toString();
        window.history.replaceState({}, '', `${location.pathname}${newSearch ? '?' + newSearch : ''}`);
        console.log('WhatsApp auto-auth successful');
      })
      .catch((err) => {
        console.error('WhatsApp token exchange failed:', err);
        // Continue without auth — customer can still browse the menu
      });
  }, [waToken, waAuthAttempted, isAuthenticated, setWhatsAppGuestSession, location.search, location.pathname]);

  const [categories, setCategories] = useState<MenuCategory[]>([])
  const [tableInfo, setTableInfo] = useState<{ tableId: number; tableName: string } | null>(null)
  const [vendorInfo, setVendorInfo] = useState<{
    id: number;
    name: string;
    logo?: string;
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
    coverImageUrl?: string;
    themePreset?: string;
  } | null>(null)
  const [itemQuantities, setItemQuantities] = useState<{ [id: number]: number }>({})
  const [isLoading, setIsLoading] = useState(true)
  const [showModificationWarning, setShowModificationWarning] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  // "Filter out my allergens" (UK plan Phase 4) — items containing a selected
  // allergen are hidden from the menu grid
  const [showAllergenFilter, setShowAllergenFilter] = useState(false)
  const [excludedAllergens, setExcludedAllergens] = useState<Set<string>>(new Set())
  // The allergen filter only surfaces when this menu actually declares
  // allergens on at least one dish — a menu with no declarations would offer
  // a filter that can never match anything (country-agnostic by design:
  // works for any restaurant that fills in allergen data).
  const menuHasAllergens = categories.some(
    (cat) => cat.items?.some((i) => i.allergens && i.allergens.length > 0)
  )
  // Dietary filter (veg / non-veg / vegan square-dot markers). Only surfaces
  // when the menu declares a dietary type on at least one dish — mirrors the
  // allergen-filter convention. VEG includes VEGAN (a vegan dish is
  // vegetarian); VEGAN is the strict subset.
  const [dietaryFilter, setDietaryFilter] = useState<'ALL' | 'VEG' | 'NON_VEG' | 'VEGAN'>('ALL')
  const menuHasDietary = categories.some((cat) => cat.items?.some((i) => i.dietaryType))
  const matchesDietary = (i: MenuItem) => {
    if (dietaryFilter === 'ALL') return true
    if (dietaryFilter === 'VEG') return i.dietaryType === 'VEG' || i.dietaryType === 'VEGAN'
    return i.dietaryType === dietaryFilter
  }
  /** Combined customer preference filter: allergen exclusions + dietary choice. */
  const passesFilters = (i: MenuItem) =>
    !i.allergens?.some((a) => excludedAllergens.has(a)) && matchesDietary(i)
  const [searchResults, setSearchResults] = useState<MenuItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [expandedDescriptions, setExpandedDescriptions] = useState<{ [id: number]: boolean }>({})
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const [selectedPortions, setSelectedPortions] = useState<{ [itemId: number]: number }>({})
  const [selectedSpiceLevels, setSelectedSpiceLevels] = useState<{ [itemId: number]: SpiceLevel }>({})
  const [qrError, setQrError] = useState<any>(null)
  const [pendingVendorSwitch, setPendingVendorSwitch] = useState<{ id: number; name: string } | null>(null)
  const [openDropdown, setOpenDropdown] = useState<number | null>(null)
  
  // Sticky header + continuous scroll state (for modernDashboard theme)
  const [visibleCategoryId, setVisibleCategoryId] = useState<number | null>(null)
  const categoryRefs = useRef<{ [key: number]: HTMLDivElement | null }>({})
  const stickyTabsRef = useRef<HTMLDivElement | null>(null)
  const tabButtonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})
  
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { state: cartState, addToCart, removeFromCart, updateQuantity, updateItemPrice, getCartQuantity, getTotalItems, clearCart, setVendorContext } = useCart()
  const { setBranding } = useVendorBranding()
  const navigate = useNavigate()
  const { theme, currentThemeName, selectTheme } = useTheme()
  
  // Check if modern theme is active
  const isModernTheme = currentThemeName === 'modernDashboard'
  
  // Category emoji helper for sticky tabs
  const getCategoryEmoji = (name: string): string => {
    const lower = name.toLowerCase();
    if (lower.includes('appetizer') || lower.includes('starter')) return '🥗';
    if (lower.includes('beverage') || lower.includes('drink')) return '🥤';
    if (lower.includes('chinese')) return '🥡';
    if (lower.includes('indian') || lower.includes('curry')) return '🍛';
    if (lower.includes('italian') || lower.includes('pasta') || lower.includes('pizza')) return '🍕';
    if (lower.includes('dessert') || lower.includes('sweet')) return '🍰';
    if (lower.includes('breakfast')) return '🍳';
    if (lower.includes('soup')) return '🍜';
    if (lower.includes('salad')) return '🥗';
    if (lower.includes('rice') || lower.includes('biryani')) return '🍚';
    if (lower.includes('chicken')) return '🍗';
    if (lower.includes('seafood') || lower.includes('fish')) return '🐟';
    if (lower.includes('veg')) return '🥦';
    if (lower.includes('combo') || lower.includes('meal')) return '🍱';
    if (lower.includes('snack')) return '🍿';
    if (lower.includes('burger')) return '🍔';
    if (lower.includes('sandwich') || lower.includes('wrap')) return '🥪';
    if (lower.includes('noodle')) return '🍝';
    if (lower.includes('tandoor') || lower.includes('grill')) return '🔥';
    if (lower.includes('bread') || lower.includes('roti') || lower.includes('naan')) return '🫓';
    if (lower.includes('special') || lower.includes('chef')) return '⭐';
    if (lower.includes('mocktail') || lower.includes('cocktail')) return '🍹';
    if (lower.includes('coffee') || lower.includes('tea')) return '☕';
    if (lower.includes('main')) return '🍛';
    return '🍽️';
  };
  
  // Scroll-based category detection (more reliable than IntersectionObserver)
  useEffect(() => {
    if (!isModernTheme || categories.length === 0) return;
    
    // Set initial visible category
    if (!visibleCategoryId && categories.length > 0) {
      setVisibleCategoryId(categories[0].id);
    }
    
    const handleScroll = () => {
      const fixedHeadersHeight = 130; // Height of fixed headers
      
      // Find which category section is currently at the top
      let currentCategoryId: number | null = null;
      
      // Get all category sections sorted by their position
      const sections = categories.map(cat => ({
        id: cat.id,
        element: categoryRefs.current[cat.id]
      })).filter(s => s.element);
      
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        if (!section.element) continue;
        
        const rect = section.element.getBoundingClientRect();
        const sectionTop = rect.top;
        
        // If this section's top is at or above the trigger point (just below headers)
        // then this is the current section
        if (sectionTop <= fixedHeadersHeight + 50) {
          currentCategoryId = section.id;
        }
      }
      
      // Special case: if we're at the bottom of the page, select the last category
      const isAtBottom = (window.innerHeight + window.scrollY) >= document.body.scrollHeight - 100;
      if (isAtBottom && sections.length > 0) {
        currentCategoryId = sections[sections.length - 1].id;
      }
      
      // Default to first category if none found
      if (!currentCategoryId && sections.length > 0) {
        currentCategoryId = sections[0].id;
      }
      
      if (currentCategoryId && currentCategoryId !== visibleCategoryId) {
        setVisibleCategoryId(currentCategoryId);
      }
    };
    
    // Throttle scroll handler for performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    // Initial detection
    setTimeout(handleScroll, 100);
    
    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledScroll);
  }, [isModernTheme, categories, visibleCategoryId]);
  
  // Auto-scroll the category tabs to show the active category
  useEffect(() => {
    if (!isModernTheme || !visibleCategoryId) return;
    
    const tabButton = tabButtonRefs.current[visibleCategoryId];
    const tabsContainer = stickyTabsRef.current?.querySelector('.overflow-x-auto');
    
    if (tabButton && tabsContainer) {
      // Calculate scroll position to center the active tab
      const containerRect = tabsContainer.getBoundingClientRect();
      const buttonRect = tabButton.getBoundingClientRect();
      const scrollLeft = tabButton.offsetLeft - (containerRect.width / 2) + (buttonRect.width / 2);
      
      tabsContainer.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
    }
  }, [visibleCategoryId, isModernTheme]);
  
  // Scroll to category on click (for fixed tabs)
  const scrollToCategory = (categoryId: number) => {
    const ref = categoryRefs.current[categoryId];
    if (ref) {
      const fixedHeadersHeight = 120; // Height of fixed vendor header (~60px) + category tabs (~55px) + buffer
      const y = ref.getBoundingClientRect().top + window.pageYOffset - fixedHeadersHeight;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };
  
  // Fuzzy search configuration
  const fuseOptions = {
    keys: [
      { name: 'name', weight: 0.7 },           // Item name is more important
      { name: 'description', weight: 0.3 }     // Description is less important
    ],
    threshold: 0.3,              // 0.3 = stricter matching (filters irrelevant results while still catching typos)
    distance: 100,               // How far to search in text
    minMatchCharLength: 2,       // Start matching after 2 characters
    includeScore: true,          // Include relevance score for sorting
    ignoreLocation: true,        // Search entire string, not just start
    useExtendedSearch: false     // Keep it simple for better performance
  };

  // Create Fuse instance - memoized to avoid recreation on every render
  const fuse = useMemo(() => {
    // Flatten all items from all categories with category context
    const allItems = categories.flatMap(category =>
      category.items.map(item => ({
        ...item,
        categoryName: category.name,
        categoryId: category.id
      }))
    );

    return new Fuse(allItems, fuseOptions);
  }, [categories]); // Recreate only when categories change

  // Track when portions change to update cart prices
  const handlePortionChange = (itemId: number, portionId: number) => {
    setSelectedPortions(prev => ({ ...prev, [itemId]: portionId }));
    
    // If item is in cart, update its price
    const cartQuantity = getCartQuantity(itemId);
    if (cartQuantity > 0) {
      // Find the item and its new price based on selected portion
      const item = categories
        .flatMap(cat => cat.items)
        .find(item => item.id === itemId);
        
      if (item && item.portions) {
        const selectedPortion = item.portions.find(p => p.id === portionId);
        if (selectedPortion) {
          console.log(`Updating cart price for ${item.name} to ${selectedPortion.effectivePrice} (${selectedPortion.portionName})`);
          // Update the price directly
          updateItemPrice(itemId, selectedPortion.effectivePrice);
        }
      }
    }
  };

  // Navigation guard to prevent loops
  const [lastNavigation, setLastNavigation] = React.useState<string>('');
  
  // Debug logging for navigation - ONLY redirect on exact /menu path
  useEffect(() => {
    console.log('Menu component rendered with:', { 
      vendorId, 
      vendorIdFromQuery,
      qrCode, 
      pathname: location.pathname, 
      search: location.search,
      contextVendorId: cartState.vendorId,
      contextQrCode: cartState.qrCode
    });
    
    // ONLY handle redirects for the exact /menu path without params
    // Do NOT redirect when on other paths (prevents navigation loops)
    const isExactMenuPath = location.pathname === '/menu';
    const currentNavKey = `${location.pathname}-${vendorId}-${qrCode}`;
    
    if (!isExactMenuPath) {
      console.log('Not on /menu path, skipping navigation logic');
      return;
    }
    
    // Prevent duplicate navigation
    if (lastNavigation === currentNavKey) {
      console.log('Duplicate navigation prevented:', currentNavKey);
      return;
    }
    
    // Check if we have vendor context from either path params or query params
    const hasVendorContext = vendorId || vendorIdFromQuery || qrCode;
    
    console.log('Navigation check:', { 
      isExactMenuPath, 
      hasVendorContext,
      vendorId,
      vendorIdFromQuery,
      qrCode 
    });
    
    if (isExactMenuPath && !hasVendorContext) {
      if (cartState.vendorId) {
        console.log('Redirecting to vendor menu with cart vendorId:', cartState.vendorId);
        setLastNavigation(currentNavKey);
        navigate(`/menu/${cartState.vendorId}`, { replace: true });
        return;
      } else if (cartState.qrCode) {
        console.log('Redirecting to menu with cart QR code:', cartState.qrCode);
        setLastNavigation(currentNavKey);
        navigate(`/menu?qr=${cartState.qrCode}`, { replace: true });
        return;
      } else {
        // When on /menu with no context, redirect to /qr
        console.log('No vendor context found, redirecting to QR page');
        setLastNavigation(currentNavKey);
        navigate('/qr', { replace: true });
      }
    }
  }, [vendorId, vendorIdFromQuery, qrCode, location.pathname, cartState.vendorId, cartState.qrCode, navigate, lastNavigation])

  // Reset categories when vendorId or qrCode changes to avoid stale data
  useEffect(() => {
    setCategories([]);
    setIsLoading(true);
  }, [vendorId, qrCode]);

  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    // Use effective vendorId/qrCode from URL params or cart context
    const effectiveQrCode = qrCode || cartState.qrCode;
    const effectiveVendorId = vendorId || vendorIdFromQuery || cartState.vendorId;
    
    console.log('Fetching menu with:', { effectiveQrCode, effectiveVendorId });
    
    // Prevent duplicate fetches
    if (isFetching) {
      console.log('Already fetching menu, skipping...');
      return;
    }
    
    if (effectiveQrCode) {
      setIsFetching(true);
      api
        .get<{
          vendorId: number;
          vendorName: string;
          vendorLogo?: string;
          tableId: number;
          tableName: string;
          qrCode: string;
          categories: MenuCategory[];
          primaryColor?: string;
          secondaryColor?: string;
          backgroundColor?: string;
          textColor?: string;
          coverImageUrl?: string;
          themePreset?: string;
          country?: string;
          currencySymbol?: string;
          currencyCode?: string;
          locale?: string;
        }>(`/qr/${effectiveQrCode}/menu`)
        .then((res) => {
          console.log('Menu loaded via QR:', res.data.categories.length, 'categories');

          // Adopt the restaurant's currency for this tab (guest QR context) so a
          // UK vendor's menu shows £ without any login. No-op on older payloads.
          setVendorScopedCurrency({
            currencySymbol: res.data.currencySymbol,
            currencyCode: res.data.currencyCode,
            locale: res.data.locale,
          });

          // VALIDATION: Check if vendorId from URL matches QR code's vendor
          const qrVendorId = res.data.vendorId;
          const urlVendorId = vendorIdFromQuery ? parseInt(vendorIdFromQuery) :
                              vendorId ? parseInt(vendorId) : null;

          // Store vendor info with branding
          setVendorInfo({
            id: qrVendorId,
            name: res.data.vendorName,
            logo: res.data.vendorLogo,
            primaryColor: res.data.primaryColor,
            secondaryColor: res.data.secondaryColor,
            backgroundColor: res.data.backgroundColor,
            textColor: res.data.textColor,
            coverImageUrl: res.data.coverImageUrl,
            themePreset: res.data.themePreset
          });
          
          // Version 2 QR codes enforce vendor validation
          if (qrVersion === '2' && urlVendorId && urlVendorId !== qrVendorId) {
            console.error('Vendor mismatch:', { urlVendorId, qrVendorId });
            setQrError({
              type: 'VENDOR_MISMATCH',
              message: 'QR code vendor mismatch',
              details: {
                expectedVendor: urlVendorId,
                actualVendor: qrVendorId,
                qrCode: effectiveQrCode
              }
            });
            setIsLoading(false);
            setIsFetching(false);
            return;
          }
          
          // CHECK FOR CART CONFLICT
          // Convert both to strings for proper comparison to avoid type mismatch issues
          const currentVendorId = cartState.vendorId ? String(cartState.vendorId) : null;
          const newVendorId = qrVendorId ? String(qrVendorId) : null;

          if (currentVendorId && newVendorId && currentVendorId !== newVendorId && getTotalItems() > 0) {
            console.log('Cart conflict detected:', {
              currentVendor: currentVendorId,
              newVendor: newVendorId,
              cartStateVendorId: cartState.vendorId,
              qrVendorId: qrVendorId,
              types: { cart: typeof cartState.vendorId, qr: typeof qrVendorId }
            });
            setPendingVendorSwitch({ id: qrVendorId, name: res.data.vendorName });
            setQrError({
              type: 'CART_CONFLICT',
              message: 'Different vendor detected',
              details: {
                currentVendorName: cartState.vendorName || 'Current Restaurant',
                newVendorName: res.data.vendorName
              }
            });
            // Store the data temporarily but don't display it yet
            setCategories([]);
            setIsLoading(false);
            setIsFetching(false);
            return;
          }

          // Log successful vendor match (no conflict)
          console.log('Vendor check passed - same vendor or new session:', {
            currentVendorId,
            newVendorId,
            isSameVendor: currentVendorId === newVendorId,
            hasItems: getTotalItems() > 0
          });

          // Store table info
          if (res.data.tableId && res.data.tableName) {
            setTableInfo({ tableId: res.data.tableId, tableName: res.data.tableName });
          }
          
          // Set vendor context in cart
          setVendorContext(qrVendorId, res.data.vendorName, effectiveQrCode);
          
          // Process menu data
          const processedData = res.data.categories.map(cat => ({
            ...cat,
            items: cat.items.map(item => {
              if (item.portions && item.portions.length > 0) {
                console.log(`Item: ${item.name}, Base Price: ${item.price}, Portions:`, item.portions);
                // Calculate effectivePrice if not present
                const processedPortions = item.portions.map(portion => {
                  const calculatedPrice = item.price + portion.priceModifier;
                  console.log(`  Portion: ${portion.portionName}, Modifier: ${portion.priceModifier}, Calculated Price: ${calculatedPrice}`);
                  return {
                    ...portion,
                    effectivePrice: portion.effectivePrice || calculatedPrice
                  };
                });
                return { ...item, portions: processedPortions };
              }
              return item;
            })
          }));
          setCategories(processedData);
          
          // Initialize selectedPortions with default portions
          const defaultPortions: { [itemId: number]: number } = {};
          processedData.forEach(cat => {
            cat.items.forEach(item => {
              if (item.portions && item.portions.length > 0) {
                const defaultPortion = item.portions.find(p => p.isDefault) || item.portions[0];
                defaultPortions[item.id] = defaultPortion.id;
              }
            });
          });
          setSelectedPortions(defaultPortions);
          
          setIsLoading(false);
          setQrError(null);
        })
        .catch((err) => {
          console.error('Failed to load menu via QR:', err);
          const error = getQRErrorType(err);
          setQrError(error);
          setCategories([]);
          setIsLoading(false);
        })
        .finally(() => {
          setIsFetching(false);
        });
    } else if (effectiveVendorId) {
      setIsFetching(true);
      api
        .get<MenuCategory[]>(`/vendors/${effectiveVendorId}/menu/categories`)
        .then((res) => {
          console.log('Menu loaded via vendorId:', res.data.length, 'categories');
          // Log portion data to debug and calculate effectivePrice if missing
          const processedData = res.data.map(cat => ({
            ...cat,
            items: cat.items.map(item => {
              if (item.portions && item.portions.length > 0) {
                console.log(`Item: ${item.name}, Base Price: ${item.price}, Portions:`, item.portions);
                // Calculate effectivePrice if not present
                const processedPortions = item.portions.map(portion => {
                  const calculatedPrice = item.price + portion.priceModifier;
                  console.log(`  Portion: ${portion.portionName}, Modifier: ${portion.priceModifier}, Calculated Price: ${calculatedPrice}`);
                  return {
                    ...portion,
                    effectivePrice: portion.effectivePrice || calculatedPrice
                  };
                });
                return { ...item, portions: processedPortions };
              }
              return item;
            })
          }));
          setCategories(processedData);
          
          // Initialize selectedPortions with default portions
          const defaultPortions: { [itemId: number]: number } = {};
          processedData.forEach(cat => {
            cat.items.forEach(item => {
              if (item.portions && item.portions.length > 0) {
                const defaultPortion = item.portions.find(p => p.isDefault) || item.portions[0];
                defaultPortions[item.id] = defaultPortion.id;
              }
            });
          });
          setSelectedPortions(defaultPortions);
          
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load vendor menu:', err);
          setCategories([]);
          setIsLoading(false);
        })
        .finally(() => {
          setIsFetching(false);
        });
    } else {
      console.log('No vendor context available for menu fetch');
      setCategories([]);
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode, vendorId, vendorIdFromQuery, cartState.qrCode, cartState.vendorId])

  useEffect(() => {
    setItemQuantities(prev => {
      const updated: { [id: number]: number } = { ...prev }
      categories.forEach(cat => {
        cat.items.forEach(item => {
          updated[item.id] = getCartQuantity(item.id)
        })
      })
      return updated
    })
  }, [cartState.items, categories, getCartQuantity])

  // Search functionality
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchQuery.trim().length > 0) { // Changed from > 2 to > 0 for better responsiveness
        performSearch()
      } else {
        setSearchResults([])
        setShowSearchResults(false)
      }
    }, 300) // 300ms debounce

    return () => clearTimeout(delaySearch)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]) // Removed categories to prevent re-searching on category load

  const performSearch = () => {
    setIsSearching(true)

    const query = searchQuery.trim()

    // If query is empty, show no results
    if (query.length === 0) {
      setSearchResults([])
      setShowSearchResults(false)
      setIsSearching(false)
      return
    }

    // Use Fuse.js for fuzzy search
    const fuseResults = fuse.search(query)

    // Extract items from Fuse results (already includes categoryName and categoryId from memoized data)
    // Results are automatically sorted by relevance score
    const results: MenuItem[] = fuseResults.map(result => result.item)

    setSearchResults(results)
    setShowSearchResults(true)
    setIsSearching(false)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults([])
    setShowSearchResults(false)
    setIsSearchExpanded(false)
  }
  
  const toggleSearch = () => {
    setIsSearchExpanded(!isSearchExpanded)
    if (!isSearchExpanded) {
      // Focus on input when expanding
      setTimeout(() => searchInputRef.current?.focus(), 100)
    } else {
      // Clear search when collapsing
      clearSearch()
    }
  }

  // Removed duplicate hydration logic - now handled centrally in CartContext

  const handleAddToCart = (item: MenuItem) => {
    const stock = item.quantity != null ? item.quantity : Infinity;
    const currentQty = getCartQuantity(item.id);
    if (currentQty >= stock) {
      return;
    }
    
    // Get selected portion or default portion
    let price = item.price;
    let portionId = selectedPortions[item.id];
    let portionName = '';
    
    if (item.portions && item.portions.length > 0) {
      if (!portionId) {
        // If no portion selected, use default
        const defaultPortion = item.portions.find(p => p.isDefault) || item.portions[0];
        portionId = defaultPortion.id;
        setSelectedPortions(prev => ({ ...prev, [item.id]: defaultPortion.id }));
      }
      
      const selectedPortion = item.portions.find(p => p.id === portionId);
      if (selectedPortion) {
        console.log(`Selected portion: ${selectedPortion.portionName}, effectivePrice: ${selectedPortion.effectivePrice}, priceModifier: ${selectedPortion.priceModifier}`);
        price = selectedPortion.effectivePrice;
        portionName = selectedPortion.portionName;
      } else {
        console.log('No selected portion found!');
      }
    }
    
    console.log("=== MENU: ADDING TO CART ===");
    console.log("Item:", { id: item.id, name: item.name, basePrice: item.price, adjustedPrice: price, portionId, portionName });
    console.log("Selected portion:", item.portions?.find(p => p.id === portionId));
    console.log("Current quantity in cart:", currentQty);
    console.log("Cart state before add:", cartState.items);

    const requestedSpiceLevel = selectedSpiceLevels[item.id] || item.spiceLevel;

    addToCart({
      id: item.id,
      name: item.name,
      price,
      portionId,
      portionName,
      requestedSpiceLevel: requestedSpiceLevel || undefined,
      dietaryType: item.dietaryType || undefined
    }, 1);
    
    // Let's check the cart state after a small delay to see if it updated
    setTimeout(() => {
      console.log("Cart state after add (delayed):", cartState.items);
      const cartItem = cartState.items.find(ci => ci.menuItem.id === item.id);
      console.log("Cart item price:", cartItem?.price, "Expected price:", price);
    }, 100);
    console.log("========================");
  }

  const handleRemoveFromCart = (itemId: number) => {
    const currentQty = getCartQuantity(itemId)
    if (currentQty > 1) {
      updateQuantity(itemId, currentQty - 1)
    } else {
      removeFromCart(itemId)
    }
  }



  const handleAddCategory = async (category: { name: string; description: string }) => {
    try {
      const response = await api.post('/categories', category);
      setCategories((prev) => [
        ...prev,
        { ...response.data, items: [] },
      ]);
    } catch (error) {
      console.error('Failed to add category:', error);
    }
  };

  const handleCheckout = () => {
    navigate('/checkout', { state: { vendorId, qrCode, cart: cartState.items } })
  }

  // Check if we should show modification warning
  useEffect(() => {
    // Show warning only if:
    // 1. We have a pending order ID
    // 2. We have items in cart (indicating active modification)
    // 3. We're not coming directly from order success page (check referrer)
    const shouldShow = Boolean(cartState.pendingOrderId &&
                              cartState.items.length > 0 &&
                              !document.referrer.includes('/order-success/'));

    setShowModificationWarning(shouldShow);
  }, [cartState.pendingOrderId, cartState.items.length]);

  // Apply vendor branding CSS variables when vendorInfo changes
  // AND share branding via context so other pages (checkout, login, etc.) can use it
  useEffect(() => {
    if (vendorInfo) {
      const root = document.documentElement;

      // Apply branding colors if provided
      if (vendorInfo.primaryColor) {
        root.style.setProperty('--vendor-primary-color', vendorInfo.primaryColor);
      }
      if (vendorInfo.secondaryColor) {
        root.style.setProperty('--vendor-secondary-color', vendorInfo.secondaryColor);
      }
      if (vendorInfo.backgroundColor) {
        root.style.setProperty('--vendor-background-color', vendorInfo.backgroundColor);
      }
      if (vendorInfo.textColor) {
        root.style.setProperty('--vendor-text-color', vendorInfo.textColor);
      }

      // Apply vendor's theme preset if set
      if (vendorInfo.themePreset) {
        selectTheme(vendorInfo.themePreset);
      }

      // Share branding via context for use across all pages
      setBranding({
        vendorId: vendorInfo.id,
        vendorName: vendorInfo.name,
        logo: vendorInfo.logo || null,
        primaryColor: vendorInfo.primaryColor || null,
        secondaryColor: vendorInfo.secondaryColor || null,
        backgroundColor: vendorInfo.backgroundColor || null,
        textColor: vendorInfo.textColor || null,
      });

      // Cleanup function to reset CSS variables when component unmounts
      return () => {
        root.style.removeProperty('--vendor-primary-color');
        root.style.removeProperty('--vendor-secondary-color');
        root.style.removeProperty('--vendor-background-color');
        root.style.removeProperty('--vendor-text-color');
      };
    }
  }, [vendorInfo, setBranding]);

  // If no vendor context and user hasn't been redirected yet,
  // show a fallback UI until the redirect happens
  if (!vendorId && !qrCode) {
    return (
      <EmptyState
        icon="📱"
        title="No vendor selected"
        subtitle="Scan a QR code to start ordering."
        actionButton={
          <PrimaryButton
            onClick={() => navigate('/scan')}
            data-testid="menu-scan-qr-btn"
            sx={{ borderRadius: '12px' }}
          >
            Scan QR Code
          </PrimaryButton>
        }
        data-testid="menu-no-vendor"
      />
    );
  }

  // Handle cart conflict resolution
  const handleClearCartAndContinue = () => {
    if (pendingVendorSwitch) {
      clearCart();
      setVendorContext(pendingVendorSwitch.id, pendingVendorSwitch.name, qrCode || '');
      setQrError(null);
      // Re-fetch the menu for the new vendor
      window.location.reload();
    }
  };

  const handleContinueWithCart = () => {
    setQrError(null);
    // Navigate to current vendor's menu
    if (cartState.vendorId) {
      navigate(`/menu/${cartState.vendorId}`);
    }
  };

  return (
    <>
      {/* CSS Animations for Modern Theme Modal */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(100px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      
    <div
      className="relative min-h-screen"
      style={{
        // Background for the outer margins/sides — use theme background
        backgroundColor: theme.background,
      }}
    >
      {/* Vendor Logo Pattern - fully visible on the side margins */}
      {vendorInfo?.logo && (
        <div
          className="fixed inset-0 pointer-events-none overflow-hidden"
          style={{ zIndex: 0 }}
          aria-hidden="true"
        >
          {/* Repeating logo pattern - full visibility, no blur */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${vendorInfo.logo})`,
              backgroundRepeat: 'repeat',
              backgroundSize: '200px',
              backgroundPosition: 'center',
              opacity: 1, // Full opacity - clearly visible logos
              transform: 'rotate(-15deg) scale(1.5)',
            }}
          />
        </div>
      )}

      {/* Main content wrapper - above background */}
      <div className="relative" style={{ zIndex: 1 }}>
      {/* QR Code Error Handler */}
      <QRCodeErrorHandler
        error={qrError}
        onClose={() => setQrError(null)}
        onClearCart={handleClearCartAndContinue}
        onContinueWithCart={handleContinueWithCart}
      />
      
      {showModificationWarning && (
        <div className="bg-yellow-50 dark:bg-yellow-900 border-l-4 border-yellow-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-200">
                You are modifying an existing order. Any items you add will be added to your current order.
              </p>
            </div>
          </div>
        </div>
      )}
      {isLoading ? (
        <LoadingScreen message="Loading menu..." />
      ) : categories.length === 0 ? (
        <EmptyState
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          }
          title="Menu Unavailable"
          subtitle="Unable to load the menu at this time. This could be because the vendor is unavailable or there was an error loading the menu."
          actionButton={
            <PrimaryButton
              onClick={() => window.location.reload()}
              data-testid="menu-try-again-btn"
              sx={{ borderRadius: '12px' }}
            >
              Try Again
            </PrimaryButton>
          }
          data-testid="menu-unavailable"
        />
      ) : (
        <>
          {/* Clean White Header - Logo/Name with Search - FIXED to viewport - HIDDEN on mobile */}
          <div
            className="fixed left-0 right-0 z-40 border-b bg-white hidden md:block"
            style={{
              top: 0,
              paddingTop: 'max(12px, calc(12px + env(safe-area-inset-top, 0px)))',
              paddingBottom: '12px',
              paddingLeft: 'max(16px, calc(16px + env(safe-area-inset-left, 0px)))',
              paddingRight: 'max(16px, calc(16px + env(safe-area-inset-right, 0px)))',
              backgroundColor: theme.components.card.background,
              borderColor: theme.components.card.border
            }}
          >
            <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-shrink-0">
                {vendorInfo?.logo ? (
                  <img
                    src={vendorInfo.logo}
                    alt={vendorInfo.name}
                    className="h-10 sm:h-12 w-auto max-w-[160px] sm:max-w-[200px] object-contain"
                  />
                ) : (
                  <h2 className="text-xl sm:text-2xl lg:text-3xl leading-tight sm:leading-tight lg:leading-tight font-bold text-gray-900">
                    {vendorInfo?.name || 'Menu'}
                  </h2>
                )}
              </div>

              <div className="flex items-center gap-2 flex-1 justify-end">
                {isSearchExpanded ? (
                  <div className="relative flex-1 max-w-md ml-auto transition-all duration-300 ease-in-out">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search menu items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent text-sm transition-all duration-300"
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          clearSearch()
                        }
                      }}
                    />
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={toggleSearch}
                    className="p-2 rounded-xl hover:bg-gray-100 transition-all duration-300"
                    title="Search menu items"
                    style={{
                      backgroundColor: 'rgba(156, 163, 175, 0.1)',
                      border: '1px solid rgba(156, 163, 175, 0.2)'
                    }}
                  >
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-600" />
                  </button>
                )}

                {isSearching && isSearchExpanded && (
                  <div className="text-sm text-gray-500">...</div>
                )}

                {/* Allergen filter toggle (UK plan Phase 4) — same styling as the
                    search toggle; hidden when the menu declares no allergens */}
                {menuHasAllergens && (
                <button
                  onClick={() => setShowAllergenFilter(!showAllergenFilter)}
                  className="p-2 rounded-xl hover:bg-gray-100 transition-all duration-300 relative"
                  title="Filter out my allergens"
                  style={{
                    backgroundColor: excludedAllergens.size > 0 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(156, 163, 175, 0.1)',
                    border: excludedAllergens.size > 0 ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid rgba(156, 163, 175, 0.2)'
                  }}
                >
                  {/* Sized exactly like the search icon (w-5 h-5) so the header
                      keeps its 60px height — the category pill bar below is
                      fixed at top-[60px] and clips if the header grows. */}
                  <span
                    className="w-5 h-5 flex items-center justify-center text-sm leading-none"
                    role="img"
                    aria-label="Allergen filter"
                  >⚠️</span>
                  {excludedAllergens.size > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {excludedAllergens.size}
                    </span>
                  )}
                </button>
                )}
              </div>
            </div>
          </div>

          {/* Allergen filter panel (UK plan Phase 4) — floating dropdown under the ⚠️
              toggle. Must be a fixed overlay (z above header z-40 and category bar
              z-30): an in-flow strip here sits BEHIND those fixed bars in the
              modern theme and looks like the button does nothing. */}
          {menuHasAllergens && showAllergenFilter && (
            <div className="fixed right-4 top-[68px] z-50 w-[28rem] max-w-[90vw] rounded-xl border border-amber-200 bg-white shadow-xl px-4 py-3">
              <p className="text-xs text-gray-500 mb-2">Hide dishes containing:</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(ALLERGEN_LABELS).map(([key, label]) => {
                  const active = excludedAllergens.has(key)
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        const next = new Set(excludedAllergens)
                        if (active) next.delete(key)
                        else next.add(key)
                        setExcludedAllergens(next)
                      }}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        active
                          ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'
                      }`}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
              <p className="text-[10px] text-gray-400 mt-2">
                Based on the restaurant's declarations. Always tell staff about serious allergies.
              </p>
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-amber-100">
                <p className="text-xs font-medium text-amber-700" aria-live="polite">
                  {excludedAllergens.size === 0
                    ? 'Select allergens to hide dishes containing them'
                    : (() => {
                        const hidden = categories.reduce(
                          (n, cat) => n + cat.items.filter((i) => i.allergens?.some((a) => excludedAllergens.has(a))).length,
                          0
                        )
                        return hidden === 0
                          ? 'No dishes on this menu declare the selected allergens'
                          : `${hidden} dish${hidden === 1 ? '' : 'es'} hidden from the menu`
                      })()}
                </p>
                <button
                  onClick={() => setShowAllergenFilter(false)}
                  className="px-3 py-1 text-xs font-semibold rounded-full bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {/* Dietary filter chips — each chip pairs the square-dot marker with
              its label, so the row doubles as the legend (no separate key
              needed). Horizontal-scrolls on narrow phones. Hidden when the menu
              declares no dietary types (mirrors the allergen-filter rule). */}
          {menuHasDietary && (
            <div className="flex items-center gap-2 px-4 pt-3 pb-1 overflow-x-auto">
              {([['ALL', 'All'], ['VEG', 'Veg'], ['NON_VEG', 'Non-veg'], ['VEGAN', 'Vegan']] as const).map(([value, label]) => {
                const active = dietaryFilter === value
                return (
                  <button
                    key={value}
                    onClick={() => setDietaryFilter(value)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${
                      active
                        ? 'bg-green-50 border-green-600 text-green-800'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    aria-pressed={active}
                  >
                    {value !== 'ALL' && <DietaryDot type={value} />}
                    {label}
                  </button>
                )
              })}
            </div>
          )}

          {/* Search Results or Regular Menu */}
          {showSearchResults && searchResults.length > 0 ? (
            <div className="px-4 py-6 max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Search Results ({searchResults.filter(passesFilters).length} items)
              </h3>
              <div className="space-y-4">
                {searchResults.filter(passesFilters).map((item) => {
                  const localQty = getCartQuantity(item.id)
                  
                  return (
                    <CardContainer
                      key={item.id}
                      sx={{ p: 2 }}
                    >
                      <div className="flex items-start gap-4">
                        <div className="rounded-md bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center w-16 h-16 flex-shrink-0 overflow-hidden">
                          {item.imageUrl ? (
                            <MenuItemImage imageUrl={item.imageUrl} itemName={item.name} size="thumbnail" />
                          ) : (
                            <span className="text-gray-400 text-2xl">🍽️</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-1.5"><DietaryDot type={item.dietaryType} />{item.name}</h4>
                                {item.spiceLevel && item.spiceLevel !== 'NONE' && (
                                  <span
                                    className="text-sm flex items-center gap-1"
                                    style={{ color: SPICE_LEVELS[item.spiceLevel].color }}
                                    title={SPICE_LEVELS[item.spiceLevel].displayName}
                                  >
                                    {SPICE_LEVELS[item.spiceLevel].icon}
                                  </span>
                                )}
                              </div>
                              {item.categoryName && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">in {item.categoryName}</p>
                              )}
                              {item.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{item.description}</p>
                              )}
                              <AllergenChips allergens={item.allergens} />
                              <p className="text-lg font-semibold mt-2" style={{ color: 'var(--color-text-primary)' }}>
                                {CURRENCY.symbol}{(() => {
                                  if (item.portions && item.portions.length > 0) {
                                    const portionId = selectedPortions[item.id];
                                    const selectedPortion = portionId 
                                      ? item.portions.find(p => p.id === portionId)
                                      : item.portions.find(p => p.isDefault) || item.portions[0];
                                    return selectedPortion ? selectedPortion.effectivePrice.toFixed(2) : item.price.toFixed(2);
                                  }
                                  return item.price.toFixed(2);
                                })()}
                              </p>
                              {item.portions && item.portions.length > 1 && (
                                <PortionSelector
                                  portions={item.portions}
                                  selectedPortionId={selectedPortions[item.id]}
                                  onChange={(portionId) => handlePortionChange(item.id, portionId)}
                                  basePrice={item.price}
                                  inline={true}
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              {localQty === 0 ? (
                                <button
                                  className="btn-add-premium-dark rounded font-medium disabled:opacity-50 transition-all"
                                  style={{ 
                                    padding: '0.25rem 0.75rem', 
                                    fontSize: '0.8rem', 
                                    minWidth: '50px',
                                    height: '32px'
                                  }}
                                  onClick={() => handleAddToCart(item)}
                                  disabled={!item.isAvailable}
                                >
                                  Add
                                </button>
                              ) : (
                                <div className="quantity-control-retro">
                                  <button
                                    onClick={() => handleRemoveFromCart(item.id)}
                                    disabled={localQty <= 0}
                                    className="btn-quantity-retro"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                                    </svg>
                                  </button>
                                  <span className="quantity-display-retro">{localQty}</span>
                                  <button
                                    onClick={() => handleAddToCart(item)}
                                    disabled={!item.isAvailable || (item.quantity != null && localQty >= item.quantity)}
                                    className="btn-quantity-retro"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          {!item.isAvailable && (
                            <p className="text-sm text-red-500 mt-1">Out of stock</p>
                          )}
                          {item.quantity != null && item.quantity > 0 && item.isAvailable && (
                            <p className="text-sm text-gray-500 mt-1">Only {item.quantity} left</p>
                          )}
                        </div>
                      </div>
                    </CardContainer>
                  )
                })}
              </div>
            </div>
          ) : showSearchResults && searchResults.length === 0 ? (
            <EmptyState
              icon="🔍"
              title={`No items found for "${searchQuery}"`}
              data-testid="menu-no-search-results"
            />
          ) : isModernTheme ? (
            /* ============ MODERN THEME: Sticky Header + Continuous Scroll ============ */
            <>
              {/* Fixed Category Tabs - positioned below the vendor header on desktop, at top on mobile */}
              <div
                ref={stickyTabsRef}
                className="fixed left-0 right-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm top-0 md:top-[60px]"
              >
                <WindowsScrollArrows className="px-3 py-2 sm:px-4 sm:py-3">
                  <div className="overflow-x-auto scrollbar-hide">
                    <div className="flex gap-2" style={{ scrollbarWidth: 'none' }}>
                      {categories.map((cat, index) => {
                        const isActive = visibleCategoryId === cat.id;
                        const modernColors = [
                          { bg: '#1E8E5A', light: '#ECFDF5' },
                          { bg: '#E0891E', light: '#FFF7ED' },
                          { bg: '#D2453C', light: '#FEF2F2' },
                          { bg: '#6D5BD0', light: '#F5F3FF' },
                          { bg: '#6D5BD0', light: '#FDF2F8' },
                          { bg: '#06B6D4', light: '#F0FDFA' },
                          { bg: '#E0891E', light: '#FFF7ED' },
                        ];
                        const catColor = modernColors[index % modernColors.length];
                        
                        return (
                          <button
                            key={cat.id}
                            ref={(el) => { tabButtonRefs.current[cat.id] = el; }}
                            onClick={() => scrollToCategory(cat.id)}
                            className={`flex-shrink-0 px-4 py-2 rounded-full font-semibold transition-all duration-200 text-sm flex items-center gap-1.5 whitespace-nowrap`}
                            style={{
                              backgroundColor: isActive ? catColor.light : '#F9FAFB',
                              color: isActive ? catColor.bg : '#6B7280',
                              border: isActive ? `2px solid ${catColor.bg}` : '1px solid #E5E7EB',
                              boxShadow: isActive ? `0 2px 8px ${catColor.bg}30` : 'none',
                            }}
                          >
                            <span className="text-base">{getCategoryEmoji(cat.name)}</span>
                            {cat.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </WindowsScrollArrows>
              </div>
              
              {/* Spacer for fixed headers - mobile: only category tabs ~48px, desktop: vendor header ~60px + category tabs ~55px */}
              <div className="h-[48px] md:h-[120px]"></div>
              
              {/* Menu Content */}
              <div
                className="bg-white"
                style={{
                  minHeight: 'calc(100vh - 120px)',
                }}
              >
              
              {/* Continuous Scroll: All Categories with Section Headers */}
              <div className="px-4 pb-32">
                {categories.map((cat, catIndex) => {
                  // Allergen filter (UK plan Phase 4): hide items containing an excluded allergen
                  const visibleItems = (excludedAllergens.size > 0 || dietaryFilter !== 'ALL')
                    ? cat.items.filter(passesFilters)
                    : cat.items;
                  const modernColors = [
                    { bg: '#1E8E5A', light: '#ECFDF5' },
                    { bg: '#E0891E', light: '#FFF7ED' },
                    { bg: '#D2453C', light: '#FEF2F2' },
                    { bg: '#6D5BD0', light: '#F5F3FF' },
                    { bg: '#6D5BD0', light: '#FDF2F8' },
                    { bg: '#06B6D4', light: '#F0FDFA' },
                    { bg: '#E0891E', light: '#FFF7ED' },
                  ];
                  const catColor = modernColors[catIndex % modernColors.length];
                  
                  return (
                    <div 
                      key={cat.id}
                      ref={(el) => { categoryRefs.current[cat.id] = el; }}
                      data-category-id={cat.id}
                      className="pt-6"
                    >
                      {/* Section Header */}
                      <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-100">
                        <span 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                          style={{ backgroundColor: catColor.light }}
                        >
                          {getCategoryEmoji(cat.name)}
                        </span>
                        <h2 className="text-xl font-bold text-gray-800">{cat.name}</h2>
                        <span className="text-sm text-gray-400">({visibleItems.length} items)</span>
                      </div>
                      
                      {/* Items Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {visibleItems.map((item) => {
                          const localQty = itemQuantities[item.id] ?? 0;
                          const itemStyle = getItemStyle(item.name);
                          const itemTotal = cartState.items
                            .filter((cartItem) => cartItem.menuItem?.id === item.id)
                            .reduce((sum, cartItem) => sum + cartItem.price * cartItem.quantity, 0);
                          const portionBreakdown = (() => {
                            const portionCounts: { [key: string]: number } = {};
                            cartState.items
                              .filter(cartItem => cartItem.menuItem?.id === item.id)
                              .forEach(cartItem => {
                                const portionName = cartItem.portionName || 'Regular';
                                const shortName = portionName.substring(0, 1);
                                portionCounts[shortName] = (portionCounts[shortName] || 0) + cartItem.quantity;
                              });
                            return Object.entries(portionCounts)
                              .map(([name, count]) => `${count}${name}`)
                              .join(', ');
                          })();

                          return (
                            <div
                              key={item.id}
                              className="bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 relative flex flex-col"
                              style={{ 
                                border: '1px solid var(--border-subtle)',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                              }}
                            >
                              {/* Out of Stock Overlay */}
                              {!item.isAvailable && (
                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20 rounded-2xl">
                                  <span className="text-white text-sm font-bold bg-red-500 px-4 py-2 rounded-full">OUT OF STOCK</span>
                                </div>
                              )}
                              
                              {/* Gradient Header with Image or Emoji */}
                              <div
                                className="h-28 sm:h-32 flex items-center justify-center relative overflow-hidden"
                                style={{ background: item.imageUrl ? undefined : `linear-gradient(135deg, ${itemStyle?.color}15 0%, ${itemStyle?.color}30 100%)` }}
                              >
                                {item.imageUrl ? (
                                  <MenuItemImage imageUrl={item.imageUrl} itemName={item.name} size="card" />
                                ) : (
                                  <span className="text-6xl sm:text-7xl drop-shadow-md">{itemStyle?.emoji}</span>
                                )}

                                {/* Spice badge */}
                                {item.spiceLevel && item.spiceLevel !== 'NONE' && (
                                  <div
                                    className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
                                    style={{ backgroundColor: 'white', color: SPICE_LEVELS[item.spiceLevel].color, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
                                  >
                                    {SPICE_LEVELS[item.spiceLevel].icon} {SPICE_LEVELS[item.spiceLevel].displayName}
                                  </div>
                                )}

                                {/* Cart badge */}
                                {localQty > 0 && (
                                  <div className="absolute top-3 left-3 bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-md">
                                    {localQty} in cart
                                  </div>
                                )}
                              </div>
                              
                              {/* Content */}
                              <div className="p-4 flex-1 flex flex-col">
                                {/* Title & Price Row */}
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <h3 className="font-bold text-gray-900 text-base sm:text-lg leading-tight line-clamp-2"><DietaryDot type={item.dietaryType} />{item.name}</h3>
                                  <span className="text-xl font-bold flex-shrink-0" style={{ color: itemStyle?.color }}>
                                    {CURRENCY.symbol}{item.portions?.length ? (item.portions.find(p => p.isDefault) || item.portions[0]).effectivePrice.toFixed(0) : item.price.toFixed(0)}
                                  </span>
                                </div>
                                
                                {/* Description */}
                                {item.description && (
                                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">{item.description}</p>
                                )}

                                {/* Allergen chips (UK plan Phase 4) */}
                                <AllergenChips allergens={item.allergens} />

                                {/* Portions info */}
                                {item.portions && item.portions.length > 1 && (
                                  <div className="text-xs text-gray-400 mb-3">
                                    {item.portions.length} sizes available
                                  </div>
                                )}
                                
                                {/* Spacer */}
                                <div className="flex-1" />
                                
                                {/* Action Button */}
                                <div className="mt-auto">
                                  {localQty === 0 ? (
                                    <button
                                      onClick={() => {
                                        if (!item.isAvailable) return;
                                        if ((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) {
                                          setOpenDropdown(item.id);
                                        } else {
                                          const portion = item.portions?.[0];
                                          addToCart({
                                            menuItem: item,
                                            price: portion?.effectivePrice ?? item.price,
                                            quantity: 1,
                                            portionId: portion?.id,
                                            portionName: portion?.portionName,
                                            spiceLevel: item.spiceLevel,
                                          });
                                          setItemQuantities(prev => ({ ...prev, [item.id]: 1 }));
                                        }
                                      }}
                                      disabled={!item.isAvailable}
                                      className="w-full py-3 rounded-xl font-bold text-white flex items-center justify-center transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                      style={{ 
                                        backgroundColor: itemStyle?.color,
                                        boxShadow: item.isAvailable ? `0 4px 12px ${itemStyle?.color}40` : 'none'
                                      }}
                                    >
                                      ADD TO CART {((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) ? '▼' : ''}
                                    </button>
                                  ) : (
                                    <div className="flex items-center justify-between bg-gray-50 rounded-xl p-2">
                                      <button
                                        onClick={() => {
                                          const newQty = localQty - 1;
                                          if (newQty <= 0) {
                                            removeFromCart(item.id);
                                            setItemQuantities(prev => ({ ...prev, [item.id]: 0 }));
                                          } else {
                                            updateQuantity(item.id, newQty);
                                            setItemQuantities(prev => ({ ...prev, [item.id]: newQty }));
                                          }
                                        }}
                                        className="w-10 h-10 rounded-lg flex items-center justify-center transition-all"
                                        style={{ backgroundColor: `${itemStyle?.color}20`, color: itemStyle?.color }}
                                      >
                                        <span className="text-xl font-bold">−</span>
                                      </button>
                                      <div className="text-center">
                                        <span className="text-lg font-bold" style={{ color: itemStyle?.color }}>{localQty}</span>
                                        {portionBreakdown && (
                                          <span className="text-xs text-gray-400 ml-1">({portionBreakdown})</span>
                                        )}
                                      </div>
                                      <button
                                        onClick={() => {
                                          if ((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) {
                                            setOpenDropdown(item.id);
                                          } else {
                                            const portion = item.portions?.[0];
                                            addToCart({
                                              menuItem: item,
                                              price: portion?.effectivePrice ?? item.price,
                                              quantity: 1,
                                              portionId: portion?.id,
                                              portionName: portion?.portionName,
                                              spiceLevel: item.spiceLevel,
                                            });
                                            setItemQuantities(prev => ({ ...prev, [item.id]: localQty + 1 }));
                                          }
                                        }}
                                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white transition-all"
                                        style={{ backgroundColor: itemStyle?.color }}
                                      >
                                        <span className="text-xl font-bold">+</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Stock warning */}
                                {item.quantity != null && item.quantity > 0 && item.quantity <= 10 && item.isAvailable && (
                                  <p className="text-xs text-amber-600 mt-2 text-center">Only {item.quantity} left!</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* MODERN THEME MODAL: Portion/Spice selector */}
                      {cat.items.map((item) => (
                        ((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) && openDropdown === item.id && (
                          <div 
                            key={`modal-${item.id}`}
                            className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-[9999]" 
                            onClick={() => setOpenDropdown(null)}
                            style={{ animation: 'fadeIn 0.15s ease-out' }}
                          >
                            <div 
                              className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto"
                              onClick={(e) => e.stopPropagation()}
                              style={{ animation: 'slideUp 0.2s ease-out' }}
                            >
                              {item.portions && item.portions.length > 1 ? (
                                <SmartPortionDropdown
                                  item={{ 
                                    id: item.id, 
                                    name: item.name, 
                                    description: item.description || '', 
                                    basePrice: item.price,
                                    portions: item.portions.map(p => ({ id: p.id, name: p.portionName, price: p.effectivePrice, qty: 0 })),
                                    allowsSpiceCustomization: item.allowsSpiceCustomization, 
                                    spiceLevel: item.spiceLevel 
                                  }}
                                  onClose={() => setOpenDropdown(null)} 
                                />
                              ) : (
                                <SimpleItemSelector
                                  item={{ 
                                    id: item.id, 
                                    name: item.name, 
                                    price: item.price, 
                                    allowsSpiceCustomization: item.allowsSpiceCustomization, 
                                    spiceLevel: item.spiceLevel 
                                  }}
                                  onClose={() => setOpenDropdown(null)} 
                                />
                              )}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
            </>
          ) : (
            /* ============ OTHER THEMES: Original CategoryLayout ============ */
            <div
              className="bg-white"
              style={{
                // White content area for the menu - sits above the branded background
                minHeight: 'calc(100vh - 60px)',
              }}
            >
            <CategoryLayout
              title=""
              categories={categories}
              vendorId={vendorId || cartState.vendorId || undefined}
              isCustomer={true}
              onAddCategory={handleAddCategory}
              renderSidebarFooter={null}
              renderItems={(cat) => (
          <>
            <section key={cat.id}>
              {/* THEME-BASED LAYOUT: Premium Grid for modernDashboard, original for others */}
              <div className={isModernTheme ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-4"}>
                {isModernTheme ? (
                  /* ============ MODERN THEME: Premium Grid Cards (Featured Style for All) ============ */
                  cat.items.filter(passesFilters).map((item) => {
                    const localQty = itemQuantities[item.id] ?? 0;
                    const itemStyle = getItemStyle(item.name);
                    const itemTotal = cartState.items
                      .filter((cartItem) => cartItem.menuItem?.id === item.id)
                      .reduce((sum, cartItem) => sum + cartItem.price * cartItem.quantity, 0);
                    const portionBreakdown = (() => {
                      const portionCounts: { [key: string]: number } = {};
                      cartState.items
                        .filter(cartItem => cartItem.menuItem?.id === item.id)
                        .forEach(cartItem => {
                          const portionName = cartItem.portionName || 'Regular';
                          const shortName = portionName.substring(0, 1);
                          portionCounts[shortName] = (portionCounts[shortName] || 0) + cartItem.quantity;
                        });
                      return Object.entries(portionCounts)
                        .map(([name, count]) => `${count}${name}`)
                        .join(', ');
                    })();

                    return (
                      <div
                        key={item.id}
                        className="bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 relative flex flex-col"
                        style={{ 
                          border: '1px solid var(--border-subtle)',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        }}
                      >
                        {/* Out of Stock Overlay */}
                        {!item.isAvailable && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20 rounded-2xl">
                            <span className="text-white text-sm font-bold bg-red-500 px-4 py-2 rounded-full">OUT OF STOCK</span>
                          </div>
                        )}
                        
                        {/* Gradient Header with Image or Emoji */}
                        <div
                          className="h-28 sm:h-32 flex items-center justify-center relative overflow-hidden"
                          style={{ background: item.imageUrl ? undefined : `linear-gradient(135deg, ${itemStyle?.color}15 0%, ${itemStyle?.color}30 100%)` }}
                        >
                          {item.imageUrl ? (
                            <MenuItemImage imageUrl={item.imageUrl} itemName={item.name} size="card" />
                          ) : (
                            <span className="text-6xl sm:text-7xl drop-shadow-md">{itemStyle?.emoji}</span>
                          )}

                          {/* Spice badge */}
                          {item.spiceLevel && item.spiceLevel !== 'NONE' && (
                            <div
                              className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
                              style={{ backgroundColor: 'white', color: SPICE_LEVELS[item.spiceLevel].color, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
                            >
                              {SPICE_LEVELS[item.spiceLevel].icon} {SPICE_LEVELS[item.spiceLevel].displayName}
                            </div>
                          )}

                          {/* Cart badge */}
                          {localQty > 0 && (
                            <div className="absolute top-3 left-3 bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-md">
                              {localQty} in cart
                            </div>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="p-4 flex-1 flex flex-col">
                          {/* Title & Price Row */}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3 className="font-bold text-gray-900 text-base sm:text-lg leading-tight line-clamp-2"><DietaryDot type={item.dietaryType} />{item.name}</h3>
                            <span className="text-xl font-bold flex-shrink-0" style={{ color: itemStyle?.color }}>
                              {CURRENCY.symbol}{item.portions?.length ? (item.portions.find(p => p.isDefault) || item.portions[0]).effectivePrice.toFixed(0) : item.price.toFixed(0)}
                            </span>
                          </div>
                          
                          {/* Description */}
                          {item.description && (
                            <p className="text-gray-500 text-sm mb-3 line-clamp-2">{item.description}</p>
                          )}
                          <AllergenChips allergens={item.allergens} />

                          {/* Portions as Pills */}
                          {item.portions && item.portions.length > 1 && (
                            <div className="flex flex-wrap gap-1.5 mb-3">
                              {item.portions.slice(0, 3).map((portion, idx) => (
                                <span 
                                  key={portion.id} 
                                  className="text-xs px-3 py-1.5 rounded-full font-semibold"
                                  style={{
                                    backgroundColor: idx === 0 ? itemStyle?.color : '#f1f5f9', /* basil-todo */
                                    color: idx === 0 ? 'white' : '#64748b',
                                  }}
                                >
                                  {portion.portionName} • {CURRENCY.symbol}{portion.effectivePrice.toFixed(0)}
                                </span>
                              ))}
                              {item.portions.length > 3 && (
                                <button 
                                  onClick={() => setOpenDropdown(item.id)}
                                  className="text-xs px-3 py-1.5 rounded-full font-semibold"
                                  style={{ backgroundColor: `${itemStyle?.color}15`, color: itemStyle?.color }}
                                >
                                  +{item.portions.length - 3} more
                                </button>
                              )}
                            </div>
                          )}
                          
                          {/* Stock warning */}
                          {item.quantity != null && item.quantity <= 10 && (
                            <p className="text-xs text-orange-500 font-semibold mb-2">⚡ Only {item.quantity} left!</p>
                          )}
                          
                          {/* Spacer to push button to bottom */}
                          <div className="flex-1"></div>
                          
                          {/* Action Row */}
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-2">
                            {/* Total if items in cart */}
                            {localQty > 0 && itemTotal > 0 ? (
                              <span className="text-sm text-gray-600">
                                Total: <span className="font-bold text-gray-900">{CURRENCY.symbol}{itemTotal.toFixed(0)}</span>
                                {portionBreakdown && <span className="text-gray-400 text-xs ml-1">({portionBreakdown})</span>}
                              </span>
                            ) : (
                              <span></span>
                            )}
                            
                            {/* Add/Edit buttons */}
                            {localQty > 0 ? (
                              item.portions && item.portions.length > 1 ? (
                                <button
                                  onClick={() => setOpenDropdown(item.id)}
                                  className="px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2 shadow-md hover:shadow-lg"
                                  style={{ backgroundColor: itemStyle?.color, color: 'white' }}
                                >
                                  <span className="bg-white/20 px-2 py-0.5 rounded">{localQty}</span>
                                  Edit
                                </button>
                              ) : (
                                <div className="flex items-center rounded-xl overflow-hidden" style={{ border: `2px solid ${itemStyle?.color}` }}>
                                  <button 
                                    onClick={() => handleRemoveFromCart(item.id)} 
                                    className="w-9 h-9 flex items-center justify-center transition-opacity hover:opacity-80"
                                    style={{ backgroundColor: `${itemStyle?.color}20`, color: itemStyle?.color }}
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" /></svg>
                                  </button>
                                  <span className="w-8 text-center font-bold bg-white" style={{ color: itemStyle?.color }}>{localQty}</span>
                                  <button 
                                    onClick={() => updateQuantity(item.id, localQty + 1)}
                                    disabled={!item.isAvailable || (item.quantity != null && localQty >= item.quantity)}
                                    className="w-9 h-9 flex items-center justify-center text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                                    style={{ backgroundColor: itemStyle?.color }}
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                                  </button>
                                </div>
                              )
                            ) : (
                              <button
                                onClick={() => {
                                  if (!item.isAvailable) return;
                                  if ((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) setOpenDropdown(item.id);
                                  else handleAddToCart(item);
                                }}
                                disabled={!item.isAvailable}
                                className="px-5 py-2.5 rounded-xl text-sm font-bold uppercase transition-all shadow-md hover:shadow-lg disabled:opacity-50 ml-auto"
                                style={{
                                  backgroundColor: item.isAvailable ? itemStyle?.color : '#e5e7eb', /* basil-todo */
                                  color: item.isAvailable ? 'white' : '#9ca3af',
                                }}
                              >
                                ADD TO CART {((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) ? '+' : ''}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  cat.items.filter(passesFilters).map((item) => {
                    const localQty = itemQuantities[item.id] ?? 0;
                    const itemStyle = getItemStyle(item.name);

                    // ============ CLASSIC THEME: Original List Layout ============
                  return (
                    <div
                      key={item.id}
                      className="shadow rounded-lg bg-[var(--color-background-surface)] item-card w-full card-responsive"
                      style={{ 
                        minHeight: '80px',
                        position: 'relative',
                        overflow: 'visible'
                      }}
                    >
                      {/* Main content row */}
                      <div className="p-responsive-md flex items-start gap-responsive-md">

                      <div
                        className="icon-container-sm rounded-md bg-gray-100 border border-gray-200 dark:border-gray-700 flex items-center justify-center overflow-hidden"
                      >
                        {item.imageUrl ? (
                          <MenuItemImage imageUrl={item.imageUrl} itemName={item.name} size="thumbnail" />
                        ) : (
                          <span className="text-gray-400 text-xl sm:text-2xl">🍽️</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3
                            className="text-base sm:text-lg font-semibold line-clamp-2"
                            title={item.name}
                          >
                            <DietaryDot type={item.dietaryType} />
                                                        {item.name}
                          </h3>
                          {item.spiceLevel && item.spiceLevel !== 'NONE' && (
                            <span
                              className="text-sm flex items-center gap-1"
                              style={{ 
                                color: SPICE_LEVELS[item.spiceLevel].color,
                                fontSize: '1rem',
                                lineHeight: '1.5',
                                display: 'inline-flex',
                                alignItems: 'center',
                                marginLeft: '4px'
                              }}
                              title={SPICE_LEVELS[item.spiceLevel].displayName}
                            >
                              {SPICE_LEVELS[item.spiceLevel].icon}
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <div className="mb-2">
                            <p
                              className={`text-gray-600 dark:text-gray-400 text-xs sm:text-sm transition-all duration-300 ${
                                expandedDescriptions[item.id] ? '' : 'line-clamp-1'
                              }`}
                            >
                              {item.description}
                            </p>
                            {item.description.length > 50 && (
                              <button
                                onClick={() => setExpandedDescriptions(prev => ({
                                  ...prev,
                                  [item.id]: !prev[item.id]
                                }))}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
                              >
                                {expandedDescriptions[item.id] ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </div>
                        )}
                        <AllergenChips allergens={item.allergens} />

                        {/* Display portion options directly on card */}
                        {item.portions && item.portions.length > 1 && (
                          <div className="mb-2">
                            <div className="flex flex-wrap gap-1.5">
                              {item.portions.map((portion) => (
                                <div
                                  key={portion.id}
                                  className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-md text-xs"
                                  style={{
                                    backgroundColor: 'rgba(241, 245, 249, 1)',
                                    border: '1px solid rgba(203, 213, 225, 0.6)',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
                                    {portion.portionName}
                                  </span>
                                  <span style={{ fontWeight: 700, color: 'var(--text-link)' }}>
                                    {CURRENCY.symbol}{portion.effectivePrice.toFixed(0)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-2 text-lg font-bold" style={{
                          color: 'var(--text-primary)',
                          fontWeight: 700,
                          marginBottom: '8px',
                          fontSize: '1.125rem'
                        }}>
                          {CURRENCY.symbol}{(() => {
                            if (item.portions && item.portions.length > 0) {
                              const portionId = selectedPortions[item.id];
                              const selectedPortion = portionId
                                ? item.portions.find(p => p.id === portionId)
                                : item.portions.find(p => p.isDefault) || item.portions[0];
                              return selectedPortion ? selectedPortion.effectivePrice.toFixed(2) : item.price.toFixed(2);
                            }
                            return item.price.toFixed(2);
                          })()}
                        </div>

                        <div className="flex items-center gap-3 mt-2">
                          <span 
                            className="text-sm font-semibold px-2.5 py-1 rounded-md"
                            style={{
                              color: item.isAvailable ? '#059669' : '#dc2626',
                              backgroundColor: item.isAvailable ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                              border: item.isAvailable ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                              whiteSpace: 'nowrap',
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}
                          >
                            {item.isAvailable ? 'Available' : 'Unavailable'}
                          </span>
                          {item.quantity != null && (
                            <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>
                              Stock: {item.quantity}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-center justify-center gap-2 flex-shrink-0">
                        {/* Add/Edit Button - Right side of item */}
                        {getCartQuantity(item.id) > 0 ? (
                          <div className="flex flex-col items-center gap-1">
                            {/* Show portion breakdown if item has portions */}
                            {item.portions && item.portions.length > 1 ? (
                              <>
                                <div className="flex items-center gap-2 bg-gray-100 px-2 py-1 rounded-lg">
                                  <div className="flex flex-col items-center">
                                    <span className="text-gray-800 font-bold text-sm">
                                      {getCartQuantity(item.id)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {(() => {
                                        // Get portion breakdown from cart
                                        const portionCounts: { [key: string]: number } = {};
                                        cartState.items
                                          .filter(cartItem => cartItem.menuItem?.id === item.id)
                                          .forEach(cartItem => {
                                            const portionName = cartItem.portionName || '';
                                            const shortName = portionName.substring(0, 1); // First letter
                                            portionCounts[shortName] = (portionCounts[shortName] || 0) + cartItem.quantity;
                                          });
                                        return Object.entries(portionCounts)
                                          .map(([name, count]) => `${count}${name}`)
                                          .join(', ');
                                      })()}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => setOpenDropdown(item.id)}
                                    className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                                    style={{
                                      backgroundColor: '#1e293b', /* basil-todo */
                                      color: 'var(--text-inverse)',
                                      border: '1px solid #334155' /* basil-todo */
                                    }}
                                  >
                                    Edit
                                  </button>
                                </div>
                              </>
                            ) : (
                              // Simple +/- controls for items without portions
                              <div className="flex items-center bg-gray-100 rounded-lg">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleRemoveFromCart(item.id);
                                  }}
                                  className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 rounded-l-lg transition"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                  </svg>
                                </button>
                                <span className="w-8 text-center font-semibold text-sm text-gray-800">
                                  {getCartQuantity(item.id)}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    const currentQty = getCartQuantity(item.id);
                                    if (!item.isAvailable || (item.quantity != null && currentQty >= item.quantity)) {
                                      return;
                                    }
                                    // If item not in cart, add it first
                                    if (currentQty === 0) {
                                      handleAddToCart(item);
                                    } else {
                                      // If already in cart, increment quantity
                                      updateQuantity(item.id, currentQty + 1);
                                    }
                                  }}
                                  disabled={!item.isAvailable || (item.quantity != null && getCartQuantity(item.id) >= item.quantity)}
                                  className="w-8 h-8 flex items-center justify-center text-white bg-gray-800 hover:bg-gray-700 rounded-r-lg transition disabled:opacity-50"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              if (!item.isAvailable) return; // Prevent action for unavailable items
                              // Show dropdown if item has multiple portions OR allows spice customization
                              if ((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) {
                                setOpenDropdown(item.id);
                              } else {
                                handleAddToCart(item);
                              }
                            }}
                            disabled={!item.isAvailable}
                            className="px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              // THE add-to-cart action on the live menu — this is the
                              // branch every diner gets at every width. Was slate
                              // #1e293b, which belonged to no palette in the system.
                              // Unavailable items keep a sunken, non-actionable look.
                              backgroundColor: item.isAvailable ? 'var(--action)' : 'var(--bg-sunken)',
                              color: item.isAvailable ? 'var(--action-text)' : 'var(--text-disabled)',
                              border: item.isAvailable
                                ? '1px solid var(--action)'
                                : '1px solid var(--border-default)',
                              minWidth: '80px'
                            }}
                          >
                            Add {((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) ? '▼' : ''}
                          </button>
                        )}
                        
                        {/* Item total display */}
                        {localQty > 0 && (
                          <div className="text-xs text-center" style={{ 
                            color: 'var(--color-text-secondary)',
                            fontWeight: 500,
                            lineHeight: 1.2,
                            marginTop: '4px'
                          }}>
                            Total: {CURRENCY.symbol}{(() => {
                              // Calculate total from actual cart items for this menu item
                              const itemTotal = cartState.items
                                .filter(cartItem => cartItem.menuItem?.id === item.id)
                                .reduce((sum, cartItem) => sum + (cartItem.price * cartItem.quantity), 0);
                              console.log("Item total calculation:", {
                                itemId: item.id,
                                cartItems: cartState.items.filter(cartItem => cartItem.menuItem?.id === item.id),
                                total: itemTotal
                              });
                              return itemTotal.toFixed(2);
                            })()}
                          </div>
                        )}
                      </div>
                      {/* End of main content row */}
                      </div>
                      
                      {/* Portion/Spice Dropdown */}
                      {((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) && openDropdown === item.id && (
                        <div
                          className="relative"
                          style={{
                            marginLeft: 'calc(60px + 12px)', // Icon width + gap
                            marginRight: '0',
                            marginTop: '8px',
                            marginBottom: '0',
                            zIndex: 9999,
                            position: 'relative'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('🛡️ Dropdown container clicked - preventing propagation');
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            console.log('🛡️ Dropdown container mousedown - preventing propagation');
                          }}
                        >
                          {/* Show SmartPortionDropdown if item has multiple portions */}
                          {item.portions && item.portions.length > 1 ? (
                            <SmartPortionDropdown
                              item={{
                                id: item.id,
                                name: item.name,
                                description: item.description || '',
                                basePrice: item.price,
                                portions: item.portions.map(p => ({
                                  id: p.id,  // ✅ Pass the real database portion ID
                                  name: p.portionName,
                                  price: p.effectivePrice,
                                  qty: 0
                                })),
                                allowsSpiceCustomization: item.allowsSpiceCustomization,
                                spiceLevel: item.spiceLevel
                              }}
                              onClose={() => setOpenDropdown(null)}
                            />
                          ) : (
                            /* Show SimpleItemSelector for items with only spice customization */
                            <SimpleItemSelector
                              item={{
                                id: item.id,
                                name: item.name,
                                price: item.price,
                                allowsSpiceCustomization: item.allowsSpiceCustomization,
                                spiceLevel: item.spiceLevel
                              }}
                              onClose={() => setOpenDropdown(null)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
                )}
              </div>
              
              {/* MODERN THEME MODAL: Rendered outside the grid to prevent clipping */}
              {isModernTheme && cat.items.map((item) => (
                ((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) && openDropdown === item.id && (
                  <div 
                    key={`modal-${item.id}`}
                    className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-[9999]" 
                    onClick={() => setOpenDropdown(null)}
                    style={{ animation: 'fadeIn 0.15s ease-out' }}
                  >
                    <div 
                      className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                      style={{ animation: 'slideUp 0.2s ease-out' }}
                    >
                      {item.portions && item.portions.length > 1 ? (
                        <SmartPortionDropdown
                          item={{ 
                            id: item.id, 
                            name: item.name, 
                            description: item.description || '', 
                            basePrice: item.price,
                            portions: item.portions.map(p => ({ id: p.id, name: p.portionName, price: p.effectivePrice, qty: 0 })),
                            allowsSpiceCustomization: item.allowsSpiceCustomization, 
                            spiceLevel: item.spiceLevel 
                          }}
                          onClose={() => setOpenDropdown(null)} 
                        />
                      ) : (
                        <SimpleItemSelector
                          item={{ 
                            id: item.id, 
                            name: item.name, 
                            price: item.price, 
                            allowsSpiceCustomization: item.allowsSpiceCustomization, 
                            spiceLevel: item.spiceLevel 
                          }}
                          onClose={() => setOpenDropdown(null)} 
                        />
                      )}
                    </div>
                  </div>
                )
              ))}
            </section>
            {/* Add padding at the bottom to prevent content from being hidden behind fixed cart footer on mobile */}
            <div 
              className="md:pb-0"
              style={{
                paddingBottom: 'max(128px, calc(128px + env(safe-area-inset-bottom, 0px)))'
              }}
            />
          </>
        )}
            />
            </div>
          )}
        </>
      )}
      
      {/* Fixed Cart Footer for Mobile and Desktop */}
      {cartState.items.length > 0 && (
        <div 
          className={`fixed md:bottom-20 left-0 right-0 shadow-xl md:mx-4 md:mb-2 flex items-center justify-between z-[9998] ${isModernTheme ? 'md:rounded-2xl' : 'md:rounded-lg border-t md:border'}`}
          style={{ 
            bottom: 0,
            padding: '16px',
            paddingBottom: 'max(16px, calc(16px + env(safe-area-inset-bottom, 0px)))',
            paddingLeft: 'max(16px, calc(16px + env(safe-area-inset-left, 0px)))',
            paddingRight: 'max(16px, calc(16px + env(safe-area-inset-right, 0px)))',
            backgroundColor: isModernTheme ? '#ffffff' : 'var(--color-background-surface)',
            borderColor: isModernTheme ? 'transparent' : 'var(--color-border, #e5e7eb)',
            boxShadow: isModernTheme ? '0 -4px 20px rgba(0,0,0,0.1)' : undefined
          }}
        >
          <div className="flex flex-col">
            <span className={`text-xs ${isModernTheme ? 'text-emerald-600 font-medium' : 'text-gray-500'}`}>
              {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'}
            </span>
            <span className={`font-bold ${isModernTheme ? 'text-lg text-gray-900' : 'text-sm md:text-base'}`} style={{
              color: isModernTheme ? '#111827' : 'var(--color-text-primary)'
            }}>
              Subtotal: {CURRENCY.symbol}{(() => {
                const total = cartState.items.reduce((total, item) => total + (item.price * item.quantity), 0);
                return total.toFixed(2);
              })()}
            </span>
            <span className="text-xs text-gray-400 italic">
              + taxes & charges
            </span>
          </div>
          <button
            className={`px-5 md:px-6 py-3 md:py-3.5 font-bold disabled:opacity-50 transition-all text-sm md:text-base flex items-center gap-2 ${isModernTheme ? 'rounded-xl shadow-lg hover:shadow-xl' : 'btn-add-premium-dark rounded-lg'}`}
            onClick={handleCheckout}
            disabled={cartState.items.length === 0}
            style={isModernTheme ? {
              background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
              color: 'white'
            } : {
              backgroundColor: theme.button.primary.solidBg || theme.button.primary.bg,
              color: theme.button.primary.text,
              borderRadius: '12px',
            }}
          >
            View Cart
            <span className={`${isModernTheme ? 'bg-white/20' : 'bg-white bg-opacity-20'} rounded-full px-2.5 py-0.5 text-xs md:text-sm font-bold`}>
              {getTotalItems()}
            </span>
          </button>
        </div>
      )}
      
      {/* Customer Request Button - show only when we have vendor, table info, and an active order */}
      {tableInfo && (vendorId || cartState.vendorId) && cartState.pendingOrderId && (
        <CustomerRequestButton
          vendorId={Number(vendorId || cartState.vendorId)}
          tableId={tableInfo.tableId}
          orderId={Number(cartState.pendingOrderId)}
        />
      )}

      </div>{/* End main content wrapper */}
    </div>
    </>
  )
}

export default Menu
